import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { fiscalYearLabel } from "@/features/purchase/constants";
import {
  computeLineAmounts,
  computePoTotals,
  type ComputedLine,
} from "@/features/purchase/api";
import type { QuotationFormValues } from "@/features/quotations/schema";
import type {
  Quotation,
  QuotationLine,
  QuotationLineTax,
  QuotationListRow,
  QuotationStatus,
} from "@/features/quotations/types";
import type { TaxRate } from "@/features/settings/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrateQt(raw: Quotation): Quotation {
  return {
    ...raw,
    subtotal: money(raw.subtotal),
    discount: money(raw.discount),
    tax_total: money(raw.tax_total),
    adjustment: money(raw.adjustment),
    grand_total: money(raw.grand_total),
  };
}

function hydrateLine(raw: QuotationLine): QuotationLine {
  return {
    ...raw,
    qty: money(raw.qty),
    weight: money(raw.weight),
    unit_rate: money(raw.unit_rate),
    line_subtotal: money(raw.line_subtotal),
    gst_slab: money(raw.gst_slab),
    taxes: (raw.taxes ?? []).map((t) => ({
      ...t,
      rate: money(t.rate),
      amount: money(t.amount),
    })),
  };
}

async function nextQtNumber(issueDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("quotation_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix =
    ((company?.quotation_prefix as string | undefined) ?? "QT").trim().toUpperCase() || "QT";
  const fy = fiscalYearLabel(issueDate, Number(company?.fiscal_year_start_month) || 4);
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("quotations")
    .select("qt_number")
    .ilike("qt_number", `${pattern}%`)
    .order("qt_number", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.qt_number as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  const next = (Number.isNaN(n) ? 0 : n) + 1;
  return `${pattern}${String(next).padStart(4, "0")}`;
}

async function loadClientPos(clientId: string) {
  const { data, error } = await supabase
    .from("clients")
    .select("place_of_supply, state, payment_terms, name, code, addr1, city, pincode")
    .eq("id", clientId)
    .single();
  if (error) throw error;
  return data as {
    place_of_supply: string | null;
    state: string;
    payment_terms: string;
    name: string;
    code: string;
    addr1: string;
    city: string;
    pincode: string;
  };
}

async function loadCompanyState() {
  const { data } = await supabase
    .from("company")
    .select("state, name, addr1, city, pincode")
    .limit(1)
    .maybeSingle();
  return data as {
    state: string | null;
    name: string | null;
    addr1: string | null;
    city: string | null;
    pincode: string | null;
  } | null;
}

async function loadTaxRates(): Promise<TaxRate[]> {
  const { data, error } = await supabase.from("tax_rates").select("*").eq("is_active", true);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    ...(r as TaxRate),
    rate: money((r as TaxRate).rate),
    gst_slab:
      (r as TaxRate).gst_slab === null || (r as TaxRate).gst_slab === undefined
        ? null
        : money((r as TaxRate).gst_slab),
  }));
}

async function replaceLines(
  qtId: string,
  values: QuotationFormValues,
  rates: TaxRate[],
  companyState: string | null | undefined,
  clientPos: string | null | undefined,
) {
  const { error: delErr } = await supabase.from("quotation_lines").delete().eq("quotation_id", qtId);
  if (delErr) throw delErr;

  const computedLines: ComputedLine[] = [];
  for (let i = 0; i < values.lines.length; i++) {
    const line = values.lines[i];
    const computed = computeLineAmounts(
      line,
      rates,
      companyState,
      clientPos,
      values.issue_date,
    );
    computedLines.push(computed);

    const { data: inserted, error: lineErr } = await supabase
      .from("quotation_lines")
      .insert({
        quotation_id: qtId,
        line_no: i + 1,
        material_id: line.material_id,
        material_grade_id: text(line.material_grade_id),
        shape_size_id: text(line.shape_size_id),
        description: text(line.description),
        qty: line.qty,
        weight: line.weight,
        uom_id: text(line.uom_id),
        unit_rate: line.unit_rate,
        line_subtotal: computed.line_subtotal,
        gst_slab: line.gst_slab,
      })
      .select("id")
      .single();
    if (lineErr) throw lineErr;

    if (computed.taxes.length) {
      const { error: taxErr } = await supabase.from("quotation_line_taxes").insert(
        computed.taxes.map((t) => ({
          line_id: inserted.id,
          tax_rate_id: t.tax_rate_id,
          tax_type: t.tax_type,
          rate: t.rate,
          amount: t.amount,
        })),
      );
      if (taxErr) throw taxErr;
    }
  }

  return computePoTotals(computedLines, values.discount, values.adjustment);
}

export function useQuotations(filters?: { clientId?: string }) {
  return useQuery({
    queryKey: ["quotations", filters?.clientId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<QuotationListRow[]> => {
      let q = supabase
        .from("quotations")
        .select("*, clients(code, name), quotation_lines(id)")
        .order("issue_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (filters?.clientId) q = q.eq("client_id", filters.clientId);
      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const client = raw.clients as { code?: string; name?: string } | null;
        const lines = raw.quotation_lines as { id: string }[] | null;
        const { clients: _c, quotation_lines: _l, ...rest } = raw;
        return {
          ...hydrateQt(rest as unknown as Quotation),
          client_code: client?.code ?? null,
          client_name: client?.name ?? null,
          line_count: lines?.length ?? 0,
        };
      });
    },
  });
}

export function useQuotation(id?: string) {
  return useQuery({
    queryKey: ["quotations", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<Quotation> => {
      const { data, error } = await supabase
        .from("quotations")
        .select(
          `*,
          clients(code, name, place_of_supply, state),
          quotation_lines(
            *,
            quotation_line_taxes(*),
            materials(name),
            material_grades(name),
            shape_sizes(size_label),
            uoms(code)
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const client = raw.clients as { code?: string; name?: string } | null;
      const linesRaw = (raw.quotation_lines as Record<string, unknown>[] | null) ?? [];
      const { clients: _c, quotation_lines: _l, ...rest } = raw;

      const lines = linesRaw
        .map((line) => {
          const taxes = (
            (line.quotation_line_taxes as QuotationLineTax[] | null) ?? []
          ).map((t) => ({ ...t, rate: money(t.rate), amount: money(t.amount) }));
          const material = line.materials as { name?: string } | null;
          const grade = line.material_grades as { name?: string } | null;
          const shape = line.shape_sizes as { size_label?: string } | null;
          const uom = line.uoms as { code?: string } | null;
          const {
            quotation_line_taxes: _t,
            materials: _m,
            material_grades: _g,
            shape_sizes: _s,
            uoms: _u,
            ...lineRest
          } = line;
          return hydrateLine({
            ...(lineRest as unknown as QuotationLine),
            taxes,
            material_name: material?.name ?? null,
            grade_name: grade?.name ?? null,
            shape_label: shape?.size_label ?? null,
            uom_code: uom?.code ?? null,
          });
        })
        .sort((a, b) => a.line_no - b.line_no);

      return {
        ...hydrateQt(rest as unknown as Quotation),
        client_code: client?.code ?? null,
        client_name: client?.name ?? null,
        lines,
      };
    },
  });
}

export function useCreateQuotation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      values,
      send = false,
    }: {
      values: QuotationFormValues;
      send?: boolean;
    }) => {
      const [client, company, rates] = await Promise.all([
        loadClientPos(values.client_id),
        loadCompanyState(),
        loadTaxRates(),
      ]);
      const qtNumber = await nextQtNumber(values.issue_date);
      const billTo = text(values.bill_to_text) ?? [client.name, client.addr1, client.city, client.pincode]
        .filter(Boolean)
        .join("\n");

      const { data: header, error } = await supabase
        .from("quotations")
        .insert({
          qt_number: qtNumber,
          client_id: values.client_id,
          issue_date: values.issue_date,
          valid_until: text(values.valid_until),
          status: send ? "sent" : "draft",
          bill_to_text: billTo,
          ship_to_text: text(values.ship_to_text),
          payment_terms: text(values.payment_terms) ?? client.payment_terms,
          notes: text(values.notes),
          terms: text(values.terms),
          discount: values.discount,
          adjustment: values.adjustment,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;

      const clientPos = client.place_of_supply || client.state;
      const totals = await replaceLines(
        header.id,
        values,
        rates,
        company?.state,
        clientPos,
      );

      const { data: updated, error: updErr } = await supabase
        .from("quotations")
        .update({ ...totals, updated_at: new Date().toISOString() })
        .eq("id", header.id)
        .select("*")
        .single();
      if (updErr) throw updErr;
      return hydrateQt(updated as Quotation);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      toast.success(vars.send ? "Quotation created and sent" : "Quotation created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create quotation"),
  });
}

export function useUpdateQuotation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
      replaceLines: doReplace,
      send = false,
    }: {
      id: string;
      values: QuotationFormValues;
      replaceLines: boolean;
      send?: boolean;
    }) => {
      const { data: existing, error: exErr } = await supabase
        .from("quotations")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const qt = hydrateQt(existing as Quotation);
      if (qt.status !== "draft" && qt.status !== "sent") {
        throw new Error("Cannot edit quotation in this status");
      }

      const [client, company, rates] = await Promise.all([
        loadClientPos(values.client_id),
        loadCompanyState(),
        loadTaxRates(),
      ]);

      const patch: Record<string, unknown> = {
        client_id: values.client_id,
        issue_date: values.issue_date,
        valid_until: text(values.valid_until),
        bill_to_text: text(values.bill_to_text),
        ship_to_text: text(values.ship_to_text),
        payment_terms: text(values.payment_terms) ?? client.payment_terms,
        notes: text(values.notes),
        terms: text(values.terms),
        discount: values.discount,
        adjustment: values.adjustment,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (send && qt.status === "draft") patch.status = "sent";

      let totals = {
        subtotal: qt.subtotal,
        tax_total: qt.tax_total,
        discount: values.discount,
        adjustment: values.adjustment,
        grand_total: qt.grand_total,
      };

      if (doReplace) {
        const clientPos = client.place_of_supply || client.state;
        totals = await replaceLines(id, values, rates, company?.state, clientPos);
        Object.assign(patch, totals);
      }

      const { data: updated, error } = await supabase
        .from("quotations")
        .update(patch)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return hydrateQt(updated as Quotation);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      await queryClient.invalidateQueries({ queryKey: ["quotations", vars.id] });
      toast.success(vars.send ? "Quotation saved and sent" : "Quotation saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save quotation"),
  });
}

export function useUpdateQuotationStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: QuotationStatus }) => {
      const { data, error } = await supabase
        .from("quotations")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return hydrateQt(data as Quotation);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      await queryClient.invalidateQueries({ queryKey: ["quotations", vars.id] });
      toast.success("Status updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function quotationToForm(qt: Quotation): QuotationFormValues {
  return {
    client_id: qt.client_id,
    issue_date: qt.issue_date,
    valid_until: qt.valid_until ?? "",
    payment_terms: qt.payment_terms ?? "",
    bill_to_text: qt.bill_to_text ?? "",
    ship_to_text: qt.ship_to_text ?? "",
    discount: qt.discount,
    adjustment: qt.adjustment,
    notes: qt.notes ?? "",
    terms: qt.terms ?? "",
    lines: (qt.lines ?? []).map((line) => ({
      id: line.id,
      material_id: line.material_id,
      material_grade_id: line.material_grade_id ?? "",
      shape_size_id: line.shape_size_id ?? "",
      description: line.description ?? "",
      qty: line.qty,
      weight: line.weight,
      uom_id: line.uom_id ?? "",
      unit_rate: line.unit_rate,
      gst_slab: line.gst_slab,
    })),
  };
}

export function useDeleteQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error: fetchErr } = await supabase
        .from("quotations")
        .select("status")
        .eq("id", id)
        .single();
      if (fetchErr) throw fetchErr;
      if ((data as { status: QuotationStatus }).status !== "draft") {
        throw new Error("Only draft quotations can be deleted");
      }
      const { error } = await supabase.from("quotations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      toast.success("Quotation deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete quotation"),
  });
}
